import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = articles.find((a) => a.id === articleId);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的内容可能已被移除或链接无效</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类导航', href: '/category/all' },
    { label: article.categoryName, href: `/category/${article.categoryId}` },
    { label: article.title },
  ];

  const pageTitle = `${article.title} - 欧美日韩一区精选内容聚合平台`;

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.publishedAt,
          author: { '@type': 'Person', name: article.authorName },
          publisher: {
            '@type': 'Organization',
            name: '欧美日韩一区精选内容聚合平台',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.oumeirihan.com/article/${article.id}`,
          },
          image: article.imageUrl,
        }}
      />

      <Navbar />
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <article className="mt-6">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-500">{article.publishedAt}</span>
                <span className="text-xs text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
                </span>
                <span className="text-xs text-foreground-500">作者：{article.authorName}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">{article.excerpt}</p>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/60">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between pt-6 border-t border-background-200/60">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i> 点赞
                </button>
                <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
                <button className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
              </div>
              <Link
                to={`/category/${article.categoryId}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
              >
                返回{article.categoryName} <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200/60">
              <h2 className="text-xl font-bold font-heading text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}